import { useEffect, useMemo } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { useConfirmation } from 'app/components/ConfirmationContext'
import { AliasForm } from 'app/components/mapping/AliasForm'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import { UtmAlias } from 'app/types/entities'
import { showModal } from 'app/lib/modal'
import { notifySuccess } from 'app/services/notify.client'
import type { SearchResponse } from 'app/services/search'
import { logError } from 'lib/logging'
import { useUtmAliasStore } from 'app/stores/StoreContext'

export type MappingSourcesLoaderData = {
  sources: SearchResponse<UtmAlias>
  canEditMapping: boolean
}

export type UseMappingSourcesReturn = {
  sources: UtmAlias[]
  loading: boolean
  canEditMapping: boolean
  onAdd: () => void
  onEdit: (utmAlias: UtmAlias) => void
  onDelete: (id: string, workspaceId: string) => Promise<void>
  columns: ColumnDef<UtmAlias, any>[]
}

export function useMappingSources(
  getColumns: (ctx: {
    canEditMapping: boolean
    onEdit: (utmAlias: UtmAlias) => void
    onDelete: (id: string, workspaceId: string) => Promise<void>
  }) => ColumnDef<UtmAlias, any>[],
): UseMappingSourcesReturn {
  const [confirm] = useConfirmation()
  const checkPermission = useCheckPermission()
  const canEditMapping = checkPermission([`org:settings:edit-mapping`, `settings:mapping:edit`])
  const store = useUtmAliasStore()

  useEffect(() => {
    store.fetchList({ search: `type:source` })
  }, [])

  const sources = store.hits as unknown as UtmAlias[]
  const loading = store.listLoading

  const openModal = ({ title, utmAlias }: { title: string; utmAlias?: UtmAlias }) => {
    showModal(<AliasForm utmAlias={utmAlias} type="source" />, {
      title,
      isFullScreen: false,
      removePadding: true,
      useRadix: true,
    })
  }

  const onAdd = () => openModal({ title: `Add Source Alias` })
  const onEdit = (utmAlias: UtmAlias) => openModal({ title: `Edit Alias`, utmAlias })

  const onDelete = async (id: string, _workspaceId: string) => {
    await confirm({
      title: `Delete Channel Mapping`,
      description: `Are you sure you want to delete this alias?`,
      variant: `danger`,
    }).then(async () => {
      const result = await store.remove(id)
      if (result.error) {
        logError(`Error deleting medium`, result.error)
        return
      } else {
        store.fetchList({ search: `type:source` })
        notifySuccess(`Alias deleted`)
        return
      }
    })
  }

  const columns = useMemo(() => getColumns({ canEditMapping, onEdit, onDelete }), [sources, canEditMapping])

  return {
    sources,
    loading,
    canEditMapping,
    onAdd,
    onEdit,
    onDelete,
    columns,
  }
}
